import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TagService } from '../../services/tag.service';
import * as TagActions from './tag.actions';
import {catchError, map, mergeMap, switchMap, withLatestFrom, delay, concatMap, tap} from 'rxjs/operators';
import { debounceTime, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectReport } from '../../state/search/search.selectors';
import { AppState } from '../../state/app.state';
import { fetchReport } from '../../state/search/search.actions';

// Define interfaces for type safety
interface InvestmentResult {
  success: boolean;
  data?: {
    searchId: string;
    investmentLabel: string;
    investmentColor: string;
  };
  error?: string;
}

interface DelayedAction {
  type: 'DELAY_ACTION';
  delay: number;
  action: any;
}

@Injectable()
export class TagEffects {

  constructor(
    private actions$: Actions,
    private tagService: TagService,
    private store: Store<AppState>
  ) {}

  // Effect to load all tags
  loadTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagActions.loadTags),
      mergeMap(() =>
        this.tagService.getTags().pipe(
          map(response => TagActions.loadTagsSuccess({ tags: response.tags })),
          catchError(error => of(TagActions.loadTagsFailure({ error })))
        )
      )
    )
  );

  // Effect to add a tag
  addTag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagActions.addTag),
      mergeMap(action =>
        this.tagService.addTag(action.name).pipe(
          map(response => TagActions.addTagSuccess({ tag: response.tag })),
          catchError(error => of(TagActions.addTagFailure({ error })))
        )
      )
    )
  );

  // Effect to remove a tag
  removeTag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagActions.removeTag),
      mergeMap(action =>
        this.tagService.removeTag(action.name).pipe(
          map(response => TagActions.removeTagSuccess({ tag: response.tag })),
          catchError(error => of(TagActions.removeTagFailure({ error })))
        )
      )
    )
  );

  // Effect to attach a tag to a report
  attachTag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagActions.attachTag),
      mergeMap(action =>
        this.tagService.attachTag(action.searchId, action.tags).pipe(
          map(() => TagActions.attachTagSuccess({ tags: action.tags })),
          catchError(error => of(TagActions.attachTagFailure({ error })))
        )
      )
    )
  );

  // Effect to detach a tag from a report
  detachTag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagActions.detachTag),
      mergeMap(action =>
        this.tagService.detachTag(action.tag, action.tagValues, action.searchId).pipe(
          map(() => TagActions.detachTagSuccess({ tag: action.tag })),
          catchError(error => of(TagActions.detachTagFailure({ error })))
        )
      )
    )
  );

  // Simplified loadInvestment$ effect
  // Simpler debounced loadInvestment$ effect
  loadInvestment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagActions.loadInvestment),
      // Debounce to prevent multiple rapid requests
      debounceTime(300),
      // Log for debugging
      // tap(action => console.log('Loading investment data for:', action.searchId)),
      // Call the API
      switchMap(action =>
        this.tagService.getInvestmentBySearchId(action.searchId).pipe(
          // Log response for debugging
          // tap(response => console.log('Investment API response:', response)),
          map(response => {
            // Extract the data from the response
            return TagActions.loadInvestmentSuccess({
              searchId: response.data.search_id,
              investmentLabel: response.data.investment_label,
              investmentColor: response.data.investment_color
            });
          }),
          catchError(error => {
            console.error('Error loading investment data:', error);
            return of(TagActions.loadInvestmentFailure({
              error: error.message || 'Failed to load investment data'
            }));
          })
        )
      )
    )
  );

  // Effect to handle delayed actions
  handleDelayedAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType('DELAY_ACTION' as any),
      concatMap((action: DelayedAction) => {
        return of(action.action).pipe(
          delay(action.delay)
        );
      })
    )
  );
}
